import { z } from "zod";
// Tên hiển thị Apple đi CHUNG trần với PATCH /me và appleOAuthRequestSchema — một luật, một hằng.
import { FULL_NAME_MAX_LENGTH } from "./users-me.js";

/* ===========================================================================
 * US-E23.F4-0334-S05 (@UC9517) — TÀI KHOẢN LIÊN KẾT: `GET | POST | DELETE /v1/me/linked-accounts`.
 *
 * 🔴 XÂY TRÊN BẢNG `social_accounts` SẴN CÓ, KHÔNG tạo `linked_accounts` mới.
 * Precheck 31/07: `SocialAccount` đã có ĐÚNG shape task mô tả — `provider` · `providerUid` ·
 * `@@unique([provider, providerUid])` — và đang là đường ĐĂNG NHẬP thật (`/auth/oauth/*` đọc nó).
 * Bảng thứ hai nghĩa là hai nguồn sự thật về "user này đăng nhập bằng gì": gỡ ở bảng A mà bảng B
 * vẫn cho đăng nhập, hoặc ngược lại.
 *
 * 🔴 HỆ QUẢ AN TOÀN — vì đây LÀ đường đăng nhập, gỡ nó là gỡ THẬT:
 * `DELETE` bị chặn khi đó là provider CUỐI CÙNG và user CHƯA đặt mật khẩu email. Gỡ hết đường đăng
 * nhập = mất tài khoản vĩnh viễn, mà tài khoản này gắn với SỔ TIỀN của người thật (§3.8: GDPR là ẩn
 * danh, KHÔNG xoá sổ — không có đường "khôi phục" nào để cứu).
 * ========================================================================= */

/**
 * 4 provider hệ thống hỗ trợ. Mirror `SocialAccount.provider` +
 * `/auth/oauth/{zalo,google,facebook,apple}`.
 *
 * 🔴 Danh sách này PHẢI khớp `SOCIAL_PROVIDERS` (apps/api `social-profile.ts`) — thiếu một
 * provider ở đây thì người đăng nhập bằng nó **không bao giờ gỡ liên kết được** (DELETE trả 404
 * vĩnh viễn) và response `.strict()` vỡ ngay trên máy họ. Khoá bằng
 * `social-provider-coverage.spec.ts` (@UC9548), không dựa vào việc ai đó nhớ sửa cả hai chỗ.
 */
export const LINKED_ACCOUNT_PROVIDER = {
  ZALO: "zalo",
  GOOGLE: "google",
  FACEBOOK: "facebook",
  APPLE: "apple",
} as const;
export type LinkedAccountProvider =
  (typeof LINKED_ACCOUNT_PROVIDER)[keyof typeof LINKED_ACCOUNT_PROVIDER];
export const linkedAccountProviderSchema = z.enum([
  LINKED_ACCOUNT_PROVIDER.ZALO,
  LINKED_ACCOUNT_PROVIDER.GOOGLE,
  LINKED_ACCOUNT_PROVIDER.FACEBOOK,
  LINKED_ACCOUNT_PROVIDER.APPLE,
]);

/**
 * 1 tài khoản đã liên kết.
 *
 * 🔴 KHÔNG trả `providerUid`: đó là định danh của người dùng ở bên thứ ba, không cần cho việc hiển
 * thị, và lộ ra là cho phép đối chiếu chéo danh tính giữa các hệ thống. Client gỡ liên kết bằng
 * `provider` — mỗi user chỉ có tối đa MỘT tài khoản cho mỗi provider (xem `POST` bên dưới).
 */
export const linkedAccountItemSchema = z
  .object({
    provider: linkedAccountProviderSchema,
    /** Email provider cấp — hiển thị để người dùng nhận ra "đây là tài khoản nào". Có thể vắng. */
    emailAtProvider: z.string().nullable(),
    linkedAt: z.string().datetime(),
  })
  .strict();
export type LinkedAccountItem = z.infer<typeof linkedAccountItemSchema>;

/**
 * `GET /v1/me/linked-accounts`.
 *
 * `canUnlink` do SERVER tính và trả kèm — client KHÔNG tự suy từ `accounts.length > 1`. Luật thật
 * còn phụ thuộc "đã đặt mật khẩu chưa", thứ client không được biết. Để client tự suy là mở đường
 * cho nút bấm hiện lên rồi bị 409 — hoặc tệ hơn: ẩn nút trong khi thật ra gỡ được.
 */
export const linkedAccountsResponseSchema = z
  .object({
    accounts: z.array(linkedAccountItemSchema),
    /** true ⇒ user còn ít nhất một đường đăng nhập khác sau khi gỡ một cái bất kỳ. */
    canUnlink: z.boolean(),
    /** Có mật khẩu email hay không — quyết định `canUnlink` khi chỉ còn 1 provider. */
    hasPassword: z.boolean(),
  })
  .strict();
export type LinkedAccountsResponse = z.infer<typeof linkedAccountsResponseSchema>;

/**
 * `POST /v1/me/linked-accounts` — gắn thêm provider vào hồ sơ ĐANG đăng nhập.
 *
 * 🔴 DISCRIMINATED UNION THEO `provider`, KHÔNG phải một field `authCode` chung. Ba provider có ba
 * loại credential THẬT KHÁC NHAU — Zalo dùng PKCE `(code, codeVerifier, redirectUri)`, Google dùng
 * `idToken`, Facebook dùng `accessToken` (mirror `auth-social.ts`, cùng bề mặt tầng auth đang chạy).
 * Gộp thành `authCode: string` sẽ cho phép biểu diễn *"provider=google kèm codeVerifier"* — một
 * trạng thái vô nghĩa mà server phải tự đoán ý, và đoán sai ở đây là gắn nhầm danh tính.
 *
 * 🔴 KHÔNG nhận `providerUid` từ client: server tự đổi credential → uid với provider. Cho client
 * khai uid nghĩa là **bất kỳ ai cũng gắn được tài khoản MXH của người khác vào hồ sơ mình**.
 */
export const linkAccountRequestSchema = z.discriminatedUnion("provider", [
  z
    .object({
      provider: z.literal(LINKED_ACCOUNT_PROVIDER.ZALO),
      code: z.string().trim().min(1).max(2048),
      codeVerifier: z.string().trim().min(1).max(256),
      redirectUri: z.string().url(),
    })
    .strict(),
  z
    .object({
      provider: z.literal(LINKED_ACCOUNT_PROVIDER.GOOGLE),
      idToken: z.string().trim().min(1).max(4096),
    })
    .strict(),
  z
    .object({
      provider: z.literal(LINKED_ACCOUNT_PROVIDER.FACEBOOK),
      accessToken: z.string().trim().min(1).max(4096),
    })
    .strict(),
  z
    .object({
      provider: z.literal(LINKED_ACCOUNT_PROVIDER.APPLE),
      // Apple dùng identity token (JWT RS256) — mirror `appleOAuthRequestSchema` của đường đăng
      // nhập, cùng một loại credential nên cùng một luật verify.
      identityToken: z.string().trim().min(1).max(4096),
      /**
       * 🔴 BẮT BUỘC, cùng lý do và cùng luật với `appleOAuthRequestSchema` (chống REPLAY).
       *
       * Ở đường LIÊN KẾT thì hậu quả của replay còn nặng hơn đường đăng nhập: token Apple bị
       * nhặt lại có thể được gắn vào **tài khoản của kẻ tấn công**, tức chiếm luôn danh tính
       * Apple của nạn nhân cho lần đăng nhập sau. Cùng một loại credential ⇒ cùng một chốt chặn;
       * bỏ sót một đường là để lại đúng cái cửa mà đường kia vừa đóng.
       */
      nonce: z.string().min(1).max(256),
      fullName: z.string().trim().min(1).max(FULL_NAME_MAX_LENGTH).optional(),
    })
    .strict(),
]);
export type LinkAccountRequest = z.infer<typeof linkAccountRequestSchema>;

/** Code 409 — tài khoản MXH này đã gắn vào NGƯỜI KHÁC (`@@unique(provider, providerUid)`). */
export const LINKED_ACCOUNT_TAKEN_CODE = "LINKED_ACCOUNT_ALREADY_TAKEN" as const;
export const LINKED_ACCOUNT_TAKEN_MESSAGE =
  "Tài khoản này đã liên kết với một người dùng khác.";

/** Code 409 — provider này đã gắn sẵn vào chính hồ sơ đang đăng nhập (idempotent-friendly). */
export const LINKED_ACCOUNT_DUPLICATE_PROVIDER_CODE = "LINKED_ACCOUNT_DUPLICATE_PROVIDER" as const;

/**
 * 🔴 Code 409 — chặn gỡ đường đăng nhập CUỐI CÙNG khi chưa có mật khẩu.
 *
 * Đây là chốt chặn chống **tự khoá mình ra khỏi tài khoản gắn với tiền**. Không có đường khôi phục:
 * §3.8 nói GDPR là ẩn danh chứ không xoá sổ, nên tài khoản mồ côi sẽ nằm đó mãi cùng số dư của nó.
 */
export const LINKED_ACCOUNT_LAST_LOGIN_CODE = "LINKED_ACCOUNT_LAST_LOGIN_METHOD" as const;
export const LINKED_ACCOUNT_LAST_LOGIN_MESSAGE =
  "Đây là cách đăng nhập duy nhất của bạn. Hãy đặt mật khẩu trước khi gỡ liên kết.";

/** Code 404 — provider chưa từng gắn vào hồ sơ này. */
export const LINKED_ACCOUNT_NOT_FOUND_CODE = "LINKED_ACCOUNT_NOT_FOUND" as const;
